import { useAuth } from '@auth/index';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import type { FeatureFlags, SettableFeatureFlags } from '@src/types';

const HIDE_CONTENT_DOWNLOAD_BUTTON_ORGS = ['blains'];

const parseJsonOrFalse = (value: string) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error passing value:', value);
    return false;
  }
};

const showCompetitorDeltaAtom = atomWithStorage(
  'showCompetitorDelta',
  parseJsonOrFalse(globalThis.localStorage.getItem('showCompetitorDelta') ?? 'false'),
);
const showPageElementLinksAtom = atomWithStorage(
  'showPageElementLinks',
  parseJsonOrFalse(globalThis.localStorage.getItem('showPageElementLinks') ?? 'false'),
);

const requestPreReleaseDataAtom = atomWithStorage(
  'requestPreReleaseData',
  parseJsonOrFalse(globalThis.localStorage.getItem('requestPreReleaseData') ?? 'false'),
);

export function useFeatureFlags(): {
  values: FeatureFlags;
  setters: Record<keyof SettableFeatureFlags, (update: boolean) => void>;
} {
  const { orgName } = useParams();
  const { user } = useAuth();

  const [showCompetitorDelta, setShowCompetitorDelta] = useAtom(showCompetitorDeltaAtom);
  const [showPageElementLinks, setShowPageElementLinks] = useAtom(showPageElementLinksAtom);
  const [requestPreReleaseData, setRequestPreReleaseData] = useAtom(requestPreReleaseDataAtom);

  return useMemo(
    () => ({
      values: {
        hideContentDownloadButtons: orgName
          ? !requestPreReleaseData && HIDE_CONTENT_DOWNLOAD_BUTTON_ORGS.includes(orgName)
          : false,
        showFeatureFlagSettings: user?.email?.endsWith('@ocula.tech') ?? false,
        showCompetitorDelta,
        showPageElementLinks,
        requestPreReleaseData,
      },
      setters: {
        showCompetitorDelta: setShowCompetitorDelta,
        showPageElementLinks: setShowPageElementLinks,
        requestPreReleaseData: setRequestPreReleaseData,
      },
    }),
    [
      orgName,
      user,
      showCompetitorDelta,
      showPageElementLinks,
      requestPreReleaseData,
      setShowCompetitorDelta,
      setShowPageElementLinks,
      setRequestPreReleaseData,
    ],
  );
}
